import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Container, Button, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import LabTestIcon from "../../assets/Icon/LabTestIcon.png";
import { useAlert } from "../../components/Alert";
import SoilTestScoreCard from "../farmer/SoilTestScoreCard";
import FertilizerRecommendationCard from "../farmer/FertilizerRecommendationCard";
import SoilTestReportCard from "../../components/Cards/SoilTestReportCard";
import { useReactToPrint } from "react-to-print";
import SoilTestReportPrint from "./SoilTestReportPrint";
import { getAuth, isLoginFromSoilApp } from "../../globals/authentication";
import html2pdf from "html2pdf.js";

const SoilTestReport = () => {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [scores, setScores] = useState(null);
  const [recFertilizer, setRecFertilizer] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isRecLoading, setIsRecLoading] = useState(false);
  const { showAlert } = useAlert();
  const printRef = useRef(null);
  const navigate = useNavigate();
  const isSoilAppLogin = isLoginFromSoilApp();
  const isMobileApp = window.ReactNativeWebView || isSoilAppLogin;
  const [targetYield, setTargetYield] = useState("");

  useEffect(() => {
    if (reportId) {
      loadReport(reportId);
      loadFertilizerRecommendations(reportId);
    }
  }, [reportId]);

  const loadHealthScores = (farmId) => {
    api
      .get(`/farmer/get-farm-health-scores/${farmId}`)
      .then((result) => {
        if (result?.data) {
          const { data } = result.data || {};
          setScores(data);
        } else {
          showAlert("error", "Failed to load soil health scores");
          setReport(null);
        }
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load soil health scores");
      });
  };

  const loadExternalHealthScores = (reportId) => {
    api
      .get(`/soil-testing/get-soil-report-scores/${reportId}`)
      .then((result) => {
        if (result?.data?.scores) {
          const { scores } = result.data;
          setScores(scores);
        } else {
          showAlert("error", "Failed to load soil health scores");
          setReport(null);
        }
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load soil health scores");
      });
  };

  const loadReport = (report_id) => {
    api
      .get(`/soil-testing/test-report/${report_id}`)
      .then((result) => {
        if (result?.data) {
          setReport(result.data);
          let { farm_land, source } = result.data || {};
          if (source === "EXTERNAL") {
            loadExternalHealthScores(report_id);
          } else if (farm_land) {
            loadHealthScores(farm_land);
          } else {
            showAlert("error", "Farm ID not found");
          }
        } else {
          showAlert("error", "Failed to load Soil Test Report");
          setReport(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load Soil Test Reports");
        setLoading(false);
      });
  };

  const loadFertilizerRecommendations = (report_id, newTargetYield) => {
    setIsRecLoading(true);
    const yieldValue = newTargetYield || targetYield;
    const queryParams = yieldValue
      ? `report_id=${report_id}&target_yield=${yieldValue}`
      : `report_id=${report_id}`;
    api
      .get(`/soil-testing/get-soil-fertilizer-recommendations/?${queryParams}`)
      .then((result) => {
        if (result?.data) {
          setRecFertilizer(result.data.recommendations || []);
          setTargetYield(result.data.target_yield || "");
        } else {
          showAlert("error", "Failed to load soil recommendations");
          setReport(null);
        }
        setIsRecLoading(false);
      })
      .catch((err) => {
        console.error("Error: ", err);
        showAlert("error", "Failed to load soil recommendations");
        setIsRecLoading(false);
      });
  };

  const handleView = () => {
    navigate(`/soil-testing`);
  };

  const downloadReport = useReactToPrint({
    contentRef: printRef,
    title: `Soil Test Report - ${report?.id}`,
    print: async (printIframe) => {
      const document = printIframe.contentDocument;
      if (document) {
        document.body.style.margin = "0";
        document.body.style.padding = "0";
        const html = document.getElementsByTagName("html")[0];
        html.style.width = "100%";
        await html2pdf()
          .from(html)
          .set({
            filename: `SoilTestReport-${report?.id}.pdf`,
          })
          .save();
      }
    },
  });

  const handlePrint = () => {
    if (window.Android && window.Android.printJsonReport) {
      let all_report_details = {
        report: report,
        scores: scores,
        recFertilizer: recFertilizer,
      };
      let jsonString = JSON.stringify(all_report_details);
      window.Android.printJsonReport(jsonString);
      return;
    }
    showAlert("error", t("Print is not supported in this browser"));
  };

  const openInBrowser = () => {
    let auth = getAuth();
    if (!auth) {
      showAlert("error", "Unauthorized access");
      return;
    }
    let { user, role, token } = auth;
    let authDetails = {
      user: user,
      role: role,
      token: token,
      redirectionURL: `/soil-test-report/${reportId}`,
    };
    let authData = JSON.stringify(authDetails);
    let internalLoginURL = `/login-internal/?authDetails=${authData}`;
    if (isSoilAppLogin) {
      const fullUrl = `${window.location.origin}${internalLoginURL}`;
      if (window.Android && window.Android.openInBrowser === "function") {
        window.Android.openInBrowser(fullUrl);
      } else {
        window.location.href = fullUrl;
      }
    } else {
      window.open(internalLoginURL, "_blank");
    }
  };

  return (
    <>
      {!isSoilAppLogin && <CustomAppBar />}
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: report ? undefined : "calc(100vh - 9rem)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {report ? (
            <>
              <Button
                onClick={handleView}
                hideIcon
                variant="contained"
                sx={{ color: "white", marginTop: "10px" }}
              >
                {t("View all reports")}
              </Button>
              {!isMobileApp && (
                <Button
                  sx={{
                    marginTop: "10px",
                  }}
                  variant="outlined"
                  fullWidth
                  onClick={downloadReport}
                >
                  {t("Download Report")}
                </Button>
              )}
              {isMobileApp && (
                <Button
                  sx={{
                    marginTop: "10px",
                  }}
                  variant="outlined"
                  fullWidth
                  onClick={openInBrowser}
                >
                  {t("Open in Browser")}
                </Button>
              )}
              {isSoilAppLogin && (
                <Button
                  sx={{
                    marginTop: "10px",
                  }}
                  variant="outlined"
                  fullWidth
                  onClick={handlePrint}
                >
                  {t("Print Report")}
                </Button>
              )}
              <Box
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                fullWidth
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: "20px",
                    fontWeight: 700,
                    marginTop: "10px",
                  }}
                >
                  {t("Soil Test Report")}
                </Typography>
                {report && (
                  <SoilTestReportCard
                    data={{
                      [t("Soil Test ID")]: report.id,
                      [t("Farm ID")]: report.farm_land,
                      [t("Farmer Name")]:
                        report.farmer_details?.farmerName || "NA",
                      [t("Crop Name")]: report.farmer_details?.cropName || "NA",
                      [t("Farm Area")]: report.farmer_details?.farmArea || "NA",
                      [t("Nitrogen")]: report.nitrogen,
                      [t("Phosphorus")]: report.phosphorus,
                      [t("Potassium")]: report.potassium,
                      [t("pH")]: report.ph,
                      [t("Organic Carbon")]: report.organic_carbon,
                      [t("Humidity")]: report.humidity,
                      [t("temperature")]: report.temperature,
                      [t("Electric Conductivity")]:
                        report.electric_conductivity,
                      [t("Report Date")]: (report.report_time || "").split(
                        "T"
                      )[0],
                    }}
                  />
                )}
                {scores && (
                  <SoilTestScoreCard
                    scores={scores}
                    title={t("Soil Test Scores")}
                    showHealthScore={false}
                  />
                )}
                <FertilizerRecommendationCard
                  recommendations={recFertilizer}
                  reportId={reportId}
                  cropName={report.farmer_details?.cropName}
                  loadFertilizerRecommendations={loadFertilizerRecommendations}
                  targetYield={targetYield}
                  setTargetYield={setTargetYield}
                  isRecLoading={isRecLoading}
                />
              </Box>

              <div style={{ display: "none" }}>
                <div ref={printRef} className="printSection">
                  <SoilTestReportPrint
                    report={report}
                    scores={scores}
                    recFertilizer={recFertilizer}
                    t={t}
                  />
                </div>
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <div style={{ marginBottom: "30px" }}>
                <img
                  src={LabTestIcon}
                  alt="My Crops Icon"
                  style={{
                    width: "127px",
                    height: "127px",
                    marginBottom: "10px",
                  }}
                />
              </div>
              <CustomButton onClick={handleView} hideIcon>
                {t("View all reports")}
              </CustomButton>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, textAlign: "center" }}
              >
                {t("No Soil Test Report Found for this ID")}
              </Typography>
            </div>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
};

export default SoilTestReport;
